import React, {useEffect, useMemo, useState} from "react";
import axios from "axios";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import {Search, Square, SquareCheck, Lock} from "lucide-react";

import * as Constants from "../../utils/Constants";
import * as UrlConstants from "../../utils/UrlConstants";
import {getAuthToken, getClientIdOfUser, getLoggedInUser, getLoggedInUserId, showToast} from "../../utils/utility";
import {getBrowserTimeZone} from "../../utils/dateUtils";
import {getRole} from "../../utils/rolePermissions";
import {COMMON_TIMEZONES, extractErrorMessage} from "./scheduledUnlockMeta";

import SlideOver from "../../components/ui/SlideOver";
import Button from "../../components/ui/Button";
import TextField from "../../components/ui/TextField";
import Input from "../../components/ui/Input";
import Select from "../../components/ui/Select";
import Spinner from "../../components/ui/Spinner";
import Pill from "../../components/ui/Pill";
import {statusToTone} from "../assets/assetMeta";

/** Local wall-clock string with no offset, e.g. "2026-07-10T09:30:00" — exactly
 * what the backend's ScheduledUnlockTimeUtil expects alongside an IANA zone id. */
function toLocalDateTimeString(date) {
  const pad = (n) => String(n).padStart(2, "0");
  return (
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
    `T${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`
  );
}

/**
 * Slide-over: schedule a future unlock across a group of GPS locks. Also doubles as the edit
 * form for an existing SCHEDULED session when `sessionToEdit` is passed — same fields, prefilled,
 * submitting a PUT instead of a POST.
 *
 * Only GPS_LOCK assets are offered as targets — the backend silently drops
 * anything else, so keeping the picker GPS-lock-only avoids a confusing
 * "why didn't my BT lock unlock" support question.
 */
export default function CreateScheduledUnlockSlideOver({open, onClose, onCreated, sessionToEdit, presetUnitIds}) {
  const isEditing = !!sessionToEdit;
  const [locks, setLocks] = useState([]);
  const [loadingLocks, setLoadingLocks] = useState(false);
  const [search, setSearch] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);

  const [sessionName, setSessionName] = useState("");
  const [scheduledDate, setScheduledDate] = useState(null);
  const [timeZone, setTimeZone] = useState(getBrowserTimeZone());
  const [saving, setSaving] = useState(false);
  // The calendar popup isn't a Radix layer, so Escape-to-close-calendar would
  // otherwise bubble to the SlideOver's Dialog.Root (which listens on
  // `document` in the capture phase — nothing downstream can out-race that)
  // and dismiss the whole form, discarding every field. Track whether the
  // calendar is open and veto the dialog's own Escape handling while it is.
  const [datePickerOpen, setDatePickerOpen] = useState(false);

  const browserZone = useMemo(() => getBrowserTimeZone(), []);
  const timezoneOptions = useMemo(() => {
    const zones = new Set([browserZone, ...COMMON_TIMEZONES]);
    return Array.from(zones);
  }, [browserZone]);

  useEffect(() => {
    if (!open) return;
    // Reset form on every open so a previous session's picks don't linger, then prefill
    // from sessionToEdit when editing.
    setSearch("");
    setSessionName(sessionToEdit?.sessionName || "");
    setScheduledDate(sessionToEdit ? new Date(sessionToEdit.requestedLocalTime) : null);
    setTimeZone(sessionToEdit?.requestedTimezone || browserZone);
    setSelectedIds(
      sessionToEdit
        ? (sessionToEdit.executions || []).map((ex) => ex.deviceId)
        : (presetUnitIds || []) // pre-select the lock when launched from a device's Schedule Unlock
    );

    (async () => {
      try {
        setLoadingLocks(true);
        const url = `${Constants.BASE_URL}${Constants.API_V1}/${getClientIdOfUser()}/assets/${UrlConstants.GET_ALL_ASSETS}`;
        axios.defaults.headers.common["Authorization"] = `${Constants.BEARER} ${getAuthToken()}`;
        const res = await axios.get(url, {params: {page: 0, size: 1000, assetType: "GPS_LOCK"}});
        const data = res.data?.responseData || [];
        setLocks(Array.isArray(data) ? data : []);
      } catch {
        showToast("Could not load GPS locks", {type: "error"});
      } finally {
        setLoadingLocks(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, browserZone, sessionToEdit?.id]);

  const filteredLocks = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return locks;
    return locks.filter(
      (l) =>
        (l.name || "").toLowerCase().includes(q) ||
        (l.assetUnitId || "").toLowerCase().includes(q)
    );
  }, [locks, search]);

  const allFilteredSelected =
    filteredLocks.length > 0 && filteredLocks.every((l) => selectedIds.includes(l.assetUnitId));

  const toggleLock = (unitId) => {
    setSelectedIds((prev) =>
      prev.includes(unitId) ? prev.filter((id) => id !== unitId) : [...prev, unitId]
    );
  };

  const toggleSelectAllFiltered = () => {
    const filteredUnitIds = filteredLocks.map((l) => l.assetUnitId);
    setSelectedIds((prev) =>
      allFilteredSelected
        ? prev.filter((id) => !filteredUnitIds.includes(id))
        : [...new Set([...prev, ...filteredUnitIds])]
    );
  };

  const canSubmit = selectedIds.length > 0 && !!scheduledDate && !!timeZone && !saving;

  const submit = async () => {
    if (!canSubmit) return;
    try {
      setSaving(true);
      const basePath = isEditing
        ? UrlConstants.SCHEDULED_UNLOCK_DETAIL(sessionToEdit.id)
        : UrlConstants.SCHEDULED_UNLOCKS;
      const url = `${Constants.BASE_URL}${Constants.API_V1}/${getClientIdOfUser()}/${basePath}`;
      axios.defaults.headers.common["Authorization"] = `${Constants.BEARER} ${getAuthToken()}`;
      const body = {
        deviceIds: selectedIds,
        localDateTime: toLocalDateTimeString(scheduledDate),
        timeZone,
        sessionName: sessionName.trim() || undefined,
        actorId: getLoggedInUserId(),
        actorUsername: getLoggedInUser()?.userName,
        actorRole: getRole(),
      };
      const res = isEditing ? await axios.put(url, body) : await axios.post(url, body);
      showToast(
        isEditing
          ? `Scheduled unlock updated for ${res.data.totalLockCount} lock(s)`
          : `Scheduled unlock for ${res.data.totalLockCount} lock(s)`,
        {type: "success"}
      );
      onCreated && onCreated(res.data);
      onClose && onClose();
    } catch (err) {
      showToast(
        extractErrorMessage(err, isEditing ? "Could not update this scheduled unlock" : "Could not schedule the group unlock"),
        {type: "error"}
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <SlideOver
      open={open}
      onOpenChange={(o) => !o && onClose && onClose()}
      title={isEditing ? "Edit scheduled unlock" : "Schedule group unlock"}
      subtitle={
        isEditing
          ? "Update the lock set, date/time, or timezone for this scheduled unlock."
          : "Unlock a group of GPS locks at a future date and time."
      }
      size="lg"
      onEscapeKeyDown={(e) => { if (datePickerOpen) e.preventDefault(); }}
    >
      <SlideOver.Body className="space-y-5">
        <TextField
          label="Session name"
          placeholder="e.g. Warehouse A — morning unlock"
          value={sessionName}
          onChange={(e) => setSessionName(e.target.value)}
          helper="Optional — helps identify this batch later."
        />

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-1">
            <label className="block text-sm font-medium text-ink-700">
              Unlock date &amp; time<span className="text-danger-600 ml-0.5">*</span>
            </label>
            <DatePicker
              selected={scheduledDate}
              onChange={setScheduledDate}
              onCalendarOpen={() => setDatePickerOpen(true)}
              onCalendarClose={() => setDatePickerOpen(false)}
              showTimeSelect
              timeFormat="HH:mm"
              timeIntervals={15}
              dateFormat="dd/MM/yyyy HH:mm"
              placeholderText="Select date & time"
              minDate={new Date()}
              wrapperClassName="w-full"
              className="block w-full h-9 px-3 text-base rounded-md bg-white border border-ink-200 shadow-inset text-ink-900 placeholder:text-ink-400 focus:border-brand-500 focus:outline-none focus:ring"
            />
          </div>
          <div className="space-y-1">
            <label className="block text-sm font-medium text-ink-700">
              Timezone<span className="text-danger-600 ml-0.5">*</span>
            </label>
            <Select value={timeZone} onChange={(e) => setTimeZone(e.target.value)}>
              {timezoneOptions.map((tz) => (
                <option key={tz} value={tz}>
                  {tz === browserZone ? `${tz} (detected)` : tz}
                </option>
              ))}
            </Select>
          </div>
        </div>

        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="block text-sm font-medium text-ink-700">
              GPS locks<span className="text-danger-600 ml-0.5">*</span>
            </label>
            <span className="text-xs text-ink-500">{selectedIds.length} selected</span>
          </div>
          <Input
            type="search"
            placeholder="Search GPS locks by name or unit ID…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            leadingIcon={Search}
          />
          <div className="mt-2 border border-ink-200 rounded-md overflow-hidden">
            <button
              type="button"
              onClick={toggleSelectAllFiltered}
              className="w-full flex items-center gap-2 px-3 py-2 bg-ink-50 border-b border-ink-100 text-sm font-medium text-ink-700 hover:bg-ink-100"
            >
              {allFilteredSelectedIcon(allFilteredSelected)}
              {allFilteredSelected ? "Deselect all" : "Select all"} ({filteredLocks.length})
            </button>
            <div className="max-h-64 overflow-y-auto">
              {loadingLocks ? (
                <div className="py-8 flex items-center justify-center gap-3 text-ink-500">
                  <Spinner size="md" /><span className="text-sm">Loading GPS locks…</span>
                </div>
              ) : filteredLocks.length === 0 ? (
                <div className="py-8 text-center text-sm text-ink-500">
                  {locks.length === 0 ? "No GPS locks registered for this client." : "No GPS locks match your search."}
                </div>
              ) : (
                <ul className="divide-y divide-ink-100">
                  {filteredLocks.map((lock) => {
                    const checked = selectedIds.includes(lock.assetUnitId);
                    return (
                      <li key={lock.assetUnitId}>
                        <button
                          type="button"
                          onClick={() => toggleLock(lock.assetUnitId)}
                          className={
                            "w-full flex items-center gap-3 px-3 py-2 text-left transition-colors duration-fast " +
                            (checked ? "bg-brand-50" : "hover:bg-ink-50")
                          }
                        >
                          {checked ? (
                            <SquareCheck className="h-5 w-5 text-brand-700 shrink-0" />
                          ) : (
                            <Square className="h-5 w-5 text-ink-400 shrink-0" />
                          )}
                          <Lock className="h-4 w-4 text-ink-400 shrink-0" />
                          <div className="min-w-0 flex-1">
                            <div className="font-medium text-ink-900 truncate">{lock.name}</div>
                            <div className="text-xs text-ink-500 font-mono truncate">{lock.assetUnitId}</div>
                          </div>
                          <Pill tone={statusToTone(lock.assetStatus)} size="sm">{lock.assetStatus || "—"}</Pill>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </div>
        </div>
      </SlideOver.Body>
      <SlideOver.Footer>
        <Button variant="secondary" onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="primary" onClick={submit} loading={saving} disabled={!canSubmit}>
          {isEditing ? "Save changes" : "Schedule unlock"}
        </Button>
      </SlideOver.Footer>
    </SlideOver>
  );
}

function allFilteredSelectedIcon(allSelected) {
  return allSelected
    ? <SquareCheck className="h-4 w-4 text-brand-700" />
    : <Square className="h-4 w-4 text-ink-500" />;
}
